import {
  quatConjugate,
  quatMul,
  quatRotate,
  quatYaw,
  type Quat,
  type Vec3,
  wrap,
  yawQuat,
} from "../math";
import type {
  Plan,
  PlannerManifest,
  PlannerTable,
  PlannerView,
  PolicyManifest,
  ReferenceData,
  ReferenceHorizon,
} from "../types";
import { ApproachController } from "./approach";
import { Belief } from "./belief";
import { Clips, stillPlan } from "./clips";
import { ScanController } from "./scan";
import type { CubeRead } from "./sight";
import {
  makeTransition,
  scanSample,
  stillSample,
  transitionSample,
  type ReferenceSample,
  type TransitionSpec,
} from "./trajectory";

const FUTURE_STEPS = 10;
const FRAME_SKIP = 5;

interface Warp {
  psi: number;
  cube: [number, number];
}

export class PlannerRuntime {
  readonly belief: Belief;
  readonly clips: Clips;
  private readonly approach: ApproachController;
  private readonly scan: ScanController;
  private plan: Plan;
  private pending: Plan | null = null;
  private pendingAnchor: Quat | null = null;
  private pendingMatchedTargetYaw: number | null = null;
  private transition: TransitionSpec | null = null;
  private warp: Warp = { psi: 0, cube: [0, 0] };
  private age = 0;
  private anchorQuat: Quat = [1, 0, 0, 0];
  private stagedTarget: number | null = null;
  private lastSeen = -1;

  constructor(
    private readonly planner: PlannerManifest,
    private readonly table: PlannerTable,
    private readonly refs: ReferenceData,
    private readonly policy: PolicyManifest,
  ) {
    this.belief = new Belief(planner.config);
    this.clips = new Clips(table, planner.config, planner.initial_target_color);
    this.approach = new ApproachController(planner.config, refs.approach);
    this.scan = new ScanController(planner.config);
    this.plan = stillPlan(planner.config);
  }

  reset(rootQuat: ArrayLike<number>): void {
    this.clips.reset();
    this.approach.reset();
    this.scan.reset();
    this.belief.clear();
    this.plan = stillPlan(this.planner.config);
    this.pending = null;
    this.pendingAnchor = null;
    this.pendingMatchedTargetYaw = null;
    this.transition = null;
    this.age = 0;
    this.anchorQuat = yawQuat(quatYaw(rootQuat));
    this.stagedTarget = null;
    this.lastSeen = -1;
  }

  requestTarget(color: number): void {
    if (color === this.clips.targetColor && this.stagedTarget === null) return;
    this.stagedTarget = color;
  }

  update(
    read: CubeRead,
    cameraOmega: number,
    rootPos: ArrayLike<number>,
    rootQuat: ArrayLike<number>,
    cubePos: ArrayLike<number>,
    liveJointPos: ArrayLike<number>,
  ): void {
    if (read.colorOk) this.lastSeen = read.color;
    if ((this.plan.mode === "settle" || this.plan.mode === "scan")
        && cameraOmega < this.planner.config.omega_still) {
      this.belief.push(read);
    }
    const belief = this.belief.tally();
    this.clips.observe(belief);
    if (!this.finished()) return;

    if (this.plan.mode === "enter" && this.pending) {
      const next = this.pending;
      if (this.pendingAnchor) this.anchorQuat = this.pendingAnchor;
      this.plan = next;
      this.pending = null;
      this.pendingAnchor = null;
      this.transition = null;
      this.clips.commit(next);
      this.scan.commit(next);
      this.approach.commit(
        next,
        quatYaw(rootQuat),
        this.clips,
        this.pendingMatchedTargetYaw ?? undefined,
      );
      this.pendingMatchedTargetYaw = null;
      this.age = 0;
      this.belief.clear();
      return;
    }

    if (this.stagedTarget !== null) {
      this.clips.setTarget(this.stagedTarget);
      this.stagedTarget = null;
      this.approach.reset();
      this.scan.reset();
      this.belief.clear();
    }

    if (this.plan.mode === "clip" || this.plan.mode === "approach") {
      this.beginRecovery(rootQuat, liveJointPos);
      return;
    }

    let next = this.clips.decide(belief);
    next = this.approach.shape(next, belief, quatYaw(rootQuat), this.clips);
    next = this.scan.shape(next, belief, this.clips.rungIndex);

    const keepCollecting = this.plan.mode === "settle"
      && this.plan.label === "settle"
      && next.mode === "settle"
      && next.label === "settle"
      && (this.planner.config.belief_scan_after_reads ?? this.planner.config.belief_min_votes)
        > this.planner.config.belief_min_votes;
    const keepParked = Boolean(this.planner.config.approach_latch_blocked)
      && this.approach.terminal(this.plan)
      && this.approach.terminal(next)
      && this.plan.label === next.label;
    if (keepCollecting || keepParked) {
      this.age = Math.max(1, this.plan.hold);
      return;
    }

    this.age = 0;
    this.belief.clear();
    if (next.mode === "clip" || next.mode === "approach") {
      this.beginDynamic(next, belief, rootPos, rootQuat, cubePos);
      return;
    }

    this.anchorQuat = yawQuat(quatYaw(rootQuat));
    this.plan = next;
    this.scan.commit(next);
    this.approach.commit(next, quatYaw(rootQuat), this.clips);
  }

  advance(): void {
    this.age += 1;
  }

  view(): PlannerView {
    const neutral = (this.plan.mode === "settle" && this.plan.label === "settle")
      || (this.plan.mode === "scan" && this.plan.label === "scan");
    return {
      mode: this.plan.mode,
      seenColor: this.lastSeen,
      targetColor: this.clips.targetColor,
      clipLabel: neutral ? "—" : this.plan.label,
      stagedTarget: this.stagedTarget,
      solved: this.clips.solved,
      ladderPattern: this.clips.ladderPattern,
      ladderRung: this.clips.rungIndex,
      expectedTurn: this.clips.expectedTurn,
    };
  }

  horizon(): ReferenceHorizon {
    const jointPos = new Float32Array(FUTURE_STEPS * 29);
    const jointVel = new Float32Array(FUTURE_STEPS * 29);
    const pelvisQuat = new Float32Array(FUTURE_STEPS * 4);
    let rootLinVel = new Float32Array(3);
    let rootAngVel = new Float32Array(3);
    let contact = new Float32Array(12);

    for (let step = 0; step < FUTURE_STEPS; step += 1) {
      const sample = this.sample(this.age + step * FRAME_SKIP);
      jointPos.set(sample.jointPos, step * 29);
      jointVel.set(sample.jointVel, step * 29);
      pelvisQuat.set(sample.pelvisQuat, step * 4);
      if (step === 0) {
        rootLinVel = Float32Array.from(sample.rootLinVel);
        rootAngVel = Float32Array.from(sample.rootAngVel);
        contact = Float32Array.from(sample.contact);
      }
    }
    return { jointPos, jointVel, pelvisQuat, rootLinVel, rootAngVel, contact };
  }

  private sample(age: number): ReferenceSample {
    if (this.plan.mode === "enter" && this.transition) {
      const sample = transitionSample(this.transition, age);
      if (!this.planner.config.unified_transitions
          || this.planner.config.entry_angular_velocity === false) {
        sample.rootAngVel = [0, 0, 0];
      }
      return sample;
    }
    if (this.plan.mode === "clip") {
      const frame = Math.min(this.plan.exit, this.plan.entry + age);
      return this.sourceSample("clip", frame);
    }
    if (this.plan.mode === "approach") {
      return this.sourceSample("approach", Math.min(this.plan.exit, age));
    }
    const scan = scanSample(
      this.plan.yawOffset,
      this.anchorQuat,
      age,
      Math.max(1, this.plan.hold),
      this.planner.config,
    );
    return {
      ...stillSample(this.policy.action.default_joint_pos, scan.quat),
      rootAngVel: this.planner.config.unified_transitions ? [0, 0, scan.rate] : [0, 0, 0],
    };
  }

  private sourceSample(kind: "clip" | "approach", frame: number): ReferenceSample {
    const source = kind === "clip" ? this.refs : this.refs.approach;
    if (!source) throw new Error("approach reference is unavailable");
    const jointPos = Float32Array.from(source.jointPos.subarray(frame * 29, frame * 29 + 29));
    const jointVel = Float32Array.from(source.jointVel.subarray(frame * 29, frame * 29 + 29));
    const base = source.pelvisQuat.subarray(frame * 4, frame * 4 + 4);
    const pelvisQuat = quatMul(yawQuat(this.warp.psi), base);
    const rootLinVel = this.localTwist(source.pelvisLinVel, frame, pelvisQuat);
    const rootAngVel = this.localTwist(source.pelvisAngVel, frame, pelvisQuat);
    const rawContact = source.contact.subarray(frame * 12, frame * 12 + 12);
    return {
      jointPos,
      jointVel,
      pelvisQuat,
      rootLinVel,
      rootAngVel,
      contact: Float32Array.from(rawContact),
    };
  }

  private localTwist(array: Float32Array, frame: number, quat: Quat): Vec3 {
    const x = array[frame * 3] ?? 0;
    const y = array[frame * 3 + 1] ?? 0;
    const c = Math.cos(this.warp.psi);
    const s = Math.sin(this.warp.psi);
    const world = [c * x - s * y, s * x + c * y, array[frame * 3 + 2] ?? 0];
    return quatRotate(quatConjugate(quat), world);
  }

  private beginDynamic(
    next: Plan,
    belief: ReturnType<Belief["tally"]>,
    rootPos: ArrayLike<number>,
    rootQuat: ArrayLike<number>,
    cubePos: ArrayLike<number>,
  ): void {
    if (next.mode === "clip") {
      if (!belief.pose) throw new Error("clip selected without a placed cube belief");
      this.warp = {
        cube: [cubePos[0] ?? rootPos[0] ?? 0, cubePos[1] ?? rootPos[1] ?? 0],
        psi: quatYaw(rootQuat) + belief.pose.phi - (this.table.ex_phi[next.row] ?? 0)
          + next.sym * Math.PI / 2,
      };
    } else {
      const sourceQuat = this.refs.approach?.pelvisQuat.subarray(0, 4);
      if (!sourceQuat) throw new Error("approach selected without a walk reference");
      const targetYaw = wrap(quatYaw(rootQuat) + next.entryYaw);
      this.warp = {
        cube: [cubePos[0] ?? 0, cubePos[1] ?? 0],
        psi: wrap(targetYaw - quatYaw(sourceQuat)),
      };
    }
    const target = this.sourceSample(next.mode === "clip" ? "clip" : "approach", next.entry);
    // A manipulation/walk can only be chosen out of STILL/SCAN, whose held
    // joint command is the nominal stance. Match the hardware seam by
    // retaining the target frame's roll/pitch and paying only its yaw offset.
    // This avoids turning sensor tracking error into a second commanded act.
    const headingResidual = wrap(quatYaw(rootQuat) - quatYaw(target.pelvisQuat));
    const fromQuat = this.planner.config.entry_hold_source_tilt === false
      ? yawQuat(quatYaw(rootQuat))
      : quatMul(yawQuat(headingResidual), target.pelvisQuat);
    let from = stillSample(
      this.policy.action.default_joint_pos,
      fromQuat,
    );
    if (this.planner.config.entry_match_velocity === false) target.jointVel.fill(0);
    if (!this.planner.config.unified_transitions) {
      from = stillSample(this.policy.action.default_joint_pos, yawQuat(quatYaw(rootQuat)));
      target.jointVel.fill(0);
    }
    const transition = makeTransition(
      from,
      target,
      this.planner.config.enter_joint_rate,
      this.planner.config.enter_yaw_rate_deg,
      this.planner.config.lead_in_min_s,
      this.planner.config.lead_in_max_s,
    );
    this.pending = next;
    this.pendingMatchedTargetYaw = wrap(quatYaw(rootQuat) + next.matchedEntryYaw);
    this.transition = transition;
    this.plan = { ...next, mode: "enter", label: `→${next.label}`, hold: transition.steps };
  }

  private beginRecovery(
    rootQuat: ArrayLike<number>,
    liveJointPos: ArrayLike<number>,
  ): void {
    const anchor = yawQuat(quatYaw(rootQuat));
    const settle = stillPlan(this.planner.config);
    if (!this.planner.config.unified_transitions
        || this.planner.config.recovery_transitions === false) {
      this.plan = settle;
      this.anchorQuat = anchor;
      this.age = 0;
      this.belief.clear();
      return;
    }
    const transition = makeTransition(
      // Match the hardware writer's non-ramped lead-in: recover from the
      // measured joint pose, but start/end the command at zero velocity and
      // hold the live heading throughout. Feeding a clip-exit qdot into this
      // stance transition asks the policy to continue the manipulation while
      // simultaneously steering back to stand.
      stillSample(liveJointPos, anchor),
      stillSample(this.policy.action.default_joint_pos, anchor),
      this.planner.config.recovery_joint_rate ?? 1.5,
      this.planner.config.enter_yaw_rate_deg,
      this.planner.config.recovery_min_s ?? 0.2,
      this.planner.config.recovery_max_s ?? 0.6,
    );
    this.pending = settle;
    this.pendingAnchor = anchor;
    this.transition = transition;
    this.plan = { ...settle, mode: "enter", label: "→settle", hold: transition.steps };
    this.age = 0;
    this.belief.clear();
  }

  private finished(): boolean {
    if (this.plan.mode === "clip") return this.age >= this.plan.exit - this.plan.entry;
    if (this.plan.mode === "approach") return this.age >= this.plan.exit;
    return this.age >= Math.max(1, this.plan.hold);
  }
}
